import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  fetchEditaOrganizacao,
  fetchNovaOrganizacao,
  getOrganization,
} from '../../../services/Organizations/getOrganizations'
import {
  getExecutarClienteVendedor,
  getExecutarEnriquecimento,
  getExecutarIntegracao,
  getIntegracao,
  salvarIntegracao,
} from '../../../services/Organizations/getOrganizationsIntegracao'
import {
  deleteLogoOrganizacao,
  getLogoOrganizacao,
  uploadLogoOrganizacao,
} from '../../../services/Organizations/getOrganizationsLogo'
import {
  declararMarcoZero,
  getMarcoZero,
} from '../../../services/Organizations/getOrganizationsMarcoZero'
import {
  getExecutarExpurgo,
  getExecutarMixRecomendacao,
  getExecutarReconciliacao,
  getExecutarSnapshot,
  postExecutarCentralResultados,
} from '../../../services/Organizations/getOrganizationsJobs'
import {
  criarInstanciaWhatsApp,
  getExecutarIntegracaoWhatsApp,
  salvarIntegracaoWhatsApp,
} from '../../../services/Organizations/getOrganizationsIntegracaoWhatsApp'

type OrganizacaoBasica = {
  id: number
  nome: string
  tenantId: string
  ativo: boolean
  // Visibilidade (UI) dos modulos por tenant. Podem faltar em states antigos de navegacao -> tratados
  // como false ao pre-carregar os toggles.
  moduloPredictAnalyticsAtivo?: boolean
  moduloMixInteligenteAtivo?: boolean
  // Chat de suporte (widget Movidesk) nas telas do Cartivia. Ausente em states antigos de
  // navegacao -> tratado como LIGADO ao pre-carregar o toggle (default do produto).
  chatSuporteAtivo?: boolean
  // PD-0003: fase (ciclo de vida) e data de criacao do tenant.
  fase?: FaseTenant
  dataCriacao?: string | null
}

// Mesma regra do backend (OrganizacaoService.VALID_TENANT_ID_REGEXP + @Size):
// começa com letra minúscula, segue com letras/números/underscore, 3 a 52 chars.
const TENANT_ID_REGEX = /^[a-z][a-z0-9_]*$/
const TENANT_ID_MIN = 3
const TENANT_ID_MAX = 52

// O que a tela mostra sobre a logo atual. Não é o DTO: na carga da página só chegam os bytes
// (o GET devolve a imagem), então as dimensões saem da imagem carregada, e não do backend.
type LogoInfo = {
  tamanhoBytes: number
  larguraPx: number
  alturaPx: number
}

// Mesmos limites do backend (OrganizacaoLogoService): a checagem aqui é só para dar o retorno
// imediato — quem valida de verdade, inclusive o formato REAL do arquivo, é a API.
const LOGO_MAX_BYTES = 1024 * 1024
const LOGO_TIPOS_ACEITOS = ['image/png', 'image/jpeg']

const normalizaTenant = (valor: string) =>
  valor
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, TENANT_ID_MAX)

export const useOrganizacaoDetalhe = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const isNovo = !id

  const orgInicial = (location.state as { organizacao?: OrganizacaoBasica })
    ?.organizacao

  const [organizacao, setOrganizacao] = useState<OrganizacaoBasica | null>(
    orgInicial ?? null
  )
  const [carregando, setCarregando] = useState(!isNovo)
  const [salvandoDados, setSalvandoDados] = useState(false)

  const voltar = () => navigate('/adm-organizacao')

  // ---- Dados da organização ----
  const [nome, setNome] = useState(orgInicial?.nome ?? '')
  const [tenantId, setTenantId] = useState(orgInicial?.tenantId ?? '')
  // Visibilidade (UI) dos modulos por tenant. Nova org nasce com ambos desligados (opt-in por
  // contratacao); em edicao, pre-carregados a partir da org resolvida.
  const [moduloPredictAtivo, setModuloPredictAtivo] = useState(
    orgInicial?.moduloPredictAnalyticsAtivo ?? false
  )
  const [moduloMixAtivo, setModuloMixAtivo] = useState(
    orgInicial?.moduloMixInteligenteAtivo ?? false
  )
  // Chat de suporte: ao contrario dos modulos, nasce LIGADO. O toggle existe para desligar um
  // tenant especifico (cliente com canal proprio de suporte), nao para liberar caso a caso.
  // ⚠️ `undefined` (valor desconhecido) NAO vira `true` aqui: a tela prefere o state de navegacao e
  // nao refaz o GET, entao assumir "ligado" e mandar isso no PUT RELIGARIA o chat de um tenant que
  // o desligou, so por alguem ter salvo o nome. Undefined viaja ate o PUT, onde e omitido — e o
  // backend preserva o valor gravado. O default "ligado" fica so na exibicao do toggle.
  const [chatSuporteAtivo, setChatSuporteAtivo] = useState<boolean | undefined>(
    orgInicial?.chatSuporteAtivo
  )
  // PD-0003: fase (ciclo de vida) do tenant. Nova org / states antigos assumem EM_FUNCIONAMENTO
  // (mesmo default do backend), pre-carregada da org resolvida em edicao.
  const [fase, setFase] = useState<FaseTenant>(
    orgInicial?.fase ?? 'EM_FUNCIONAMENTO'
  )
  const [erroNome, setErroNome] = useState(false)
  const [erroTenantObrigatorio, setErroTenantObrigatorio] = useState(false)
  const [erroTenantInvalido, setErroTenantInvalido] = useState(false)

  const handleChangeTenant = (valor: string) =>
    setTenantId(normalizaTenant(valor))

  const criaOrganizacao = async () => {
    setSalvandoDados(true)
    const result = await fetchNovaOrganizacao({
      ativo: true,
      nome,
      tenantId,
      moduloPredictAnalyticsAtivo: moduloPredictAtivo,
      moduloMixInteligenteAtivo: moduloMixAtivo,
      chatSuporteAtivo,
    })
    if ('message' in result) {
      setSalvandoDados(false)
      toast.error(result.message)
      return
    }
    toast.success(result.info)
    // Sobe para o modo edição da org recém-criada (o POST devolve Void, então
    // resolvemos por tenantId — único) para já configurar Integração/WhatsApp/Jobs.
    const todas = await getOrganization()
    setSalvandoDados(false)
    if ('message' in todas) {
      voltar()
      return
    }
    const criada = todas.find(item => item.tenantId === tenantId)
    if (!criada) {
      voltar()
      return
    }
    navigate(`/adm-organizacao/${criada.id}`, {
      state: { organizacao: criada },
      replace: true,
    })
  }

  const editaOrganizacao = async () => {
    if (!organizacao) return
    setSalvandoDados(true)
    const result = await fetchEditaOrganizacao(organizacao.id, {
      nome,
      moduloPredictAnalyticsAtivo: moduloPredictAtivo,
      moduloMixInteligenteAtivo: moduloMixAtivo,
      chatSuporteAtivo,
      fase,
    })
    setSalvandoDados(false)
    if ('info' in result) {
      toast.success(result.info)
      setOrganizacao({
        ...organizacao,
        nome,
        moduloPredictAnalyticsAtivo: moduloPredictAtivo,
        moduloMixInteligenteAtivo: moduloMixAtivo,
        chatSuporteAtivo,
        fase,
      })
      return
    }
    toast.error(result.message)
  }

  const salvarDados = () => {
    setErroNome(false)
    setErroTenantObrigatorio(false)
    setErroTenantInvalido(false)

    if (nome.trim().length === 0) {
      setErroNome(true)
      return
    }
    if (!isNovo) {
      editaOrganizacao()
      return
    }
    if (tenantId.length === 0) {
      setErroTenantObrigatorio(true)
      return
    }
    if (tenantId.length < TENANT_ID_MIN || !TENANT_ID_REGEX.test(tenantId)) {
      setErroTenantInvalido(true)
      return
    }
    criaOrganizacao()
  }

  // ---- Logo da empresa ----
  // Aparece no cabeçalho do PDF de predição no lugar da marca Cartivia. O binário não vem no
  // DTO da organização (pesaria toda listagem): imagem por URL de objeto, metadados à parte.
  const [logoInfo, setLogoInfo] = useState<LogoInfo | null>(null)
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [carregandoLogo, setCarregandoLogo] = useState(false)
  // "Não deu para ler" ≠ "não tem logo": distinguir os dois evita anunciar que a organização
  // está sem logo quando o GET falhou, e mantém o "Remover logo" à mão justamente aí.
  const [logoIndisponivel, setLogoIndisponivel] = useState(false)
  const [enviandoLogo, setEnviandoLogo] = useState(false)
  const [removendoLogo, setRemovendoLogo] = useState(false)
  const [erroLogo, setErroLogo] = useState('')
  // A URL de objeto precisa ser revogada quando trocada/desmontada, senão o blob fica retido.
  // Fica em ref porque o cleanup do unmount não enxergaria o valor do state.
  const logoUrlRef = useRef<string | null>(null)
  // Sair da tela com o GET/POST em voo faria o `.then` criar uma URL nova DEPOIS do cleanup —
  // registrada num ref que ninguém mais limpa. Aqui ela é revogada na hora.
  const montadoRef = useRef(true)

  const aplicaLogoUrl = (url: string | null) => {
    if (!montadoRef.current) {
      if (url) URL.revokeObjectURL(url)
      return
    }
    if (logoUrlRef.current) {
      URL.revokeObjectURL(logoUrlRef.current)
    }
    logoUrlRef.current = url
    setLogoUrl(url)
  }

  // `montadoRef` é religado no corpo do efeito de propósito: com StrictMode o React monta,
  // desmonta e remonta, e um flag só desligado no cleanup deixaria a tela inerte.
  useEffect(() => {
    montadoRef.current = true
    return () => {
      montadoRef.current = false
      if (logoUrlRef.current) {
        URL.revokeObjectURL(logoUrlRef.current)
        logoUrlRef.current = null
      }
    }
  }, [])

  // O GET devolve só os bytes; as dimensões saem da própria imagem carregada, para que a
  // linha "Atual: L×A px" também apareça em quem abre a tela (e não só logo após o upload).
  const medirLogo = (blob: Blob, url: string): Promise<LogoInfo | null> =>
    new Promise(resolve => {
      const img = new Image()
      img.onload = () =>
        resolve({
          tamanhoBytes: blob.size,
          larguraPx: img.naturalWidth,
          alturaPx: img.naturalHeight,
        })
      img.onerror = () => resolve(null)
      img.src = url
    })

  const carregarLogo = async (idOrg: number) => {
    setErroLogo('')
    setLogoIndisponivel(false)
    setLogoInfo(null)
    aplicaLogoUrl(null)
    setCarregandoLogo(true)
    const result = await getLogoOrganizacao(idOrg)
    setCarregandoLogo(false)
    if (result === null) {
      // 204: organização ainda sem logo — estado normal da tela, não erro.
      return
    }
    if ('message' in result) {
      // Falha de leitura não pinta a tela de vermelho (os loaders irmãos desta página também
      // engolem falha de carga); a moldura explica e oferece nova tentativa.
      setLogoIndisponivel(true)
      return
    }
    const url = URL.createObjectURL(result)
    aplicaLogoUrl(url)
    const info = await medirLogo(result, url)
    if (montadoRef.current) setLogoInfo(info)
  }

  const recarregarLogo = () => {
    if (organizacao) carregarLogo(organizacao.id)
  }

  const enviarLogo = async (arquivo: File) => {
    if (!organizacao) return
    setErroLogo('')
    // Só barra o que o navegador soube identificar: `type` vazio (extensão não registrada no
    // SO) ou variação exótica deve seguir para a API, que decide pelo CONTEÚDO do arquivo.
    if (arquivo.type && !LOGO_TIPOS_ACEITOS.includes(arquivo.type)) {
      setErroLogo('Envie a logo em PNG ou JPG.')
      return
    }
    if (arquivo.size > LOGO_MAX_BYTES) {
      setErroLogo('A logo deve ter no máximo 1 MB.')
      return
    }
    setEnviandoLogo(true)
    const result = await uploadLogoOrganizacao(organizacao.id, arquivo)
    setEnviandoLogo(false)
    if ('message' in result) {
      setErroLogo(result.message)
      return
    }
    setLogoIndisponivel(false)
    setLogoInfo({
      tamanhoBytes: result.tamanhoBytes,
      larguraPx: result.larguraPx,
      alturaPx: result.alturaPx,
    })
    // Preview a partir do próprio arquivo enviado: evita um GET só para mostrar
    // a imagem que o navegador já tem em mãos.
    aplicaLogoUrl(URL.createObjectURL(arquivo))
    toast.success('Logo atualizada.')
  }

  const removerLogo = async () => {
    if (!organizacao) return
    setErroLogo('')
    setRemovendoLogo(true)
    const result = await deleteLogoOrganizacao(organizacao.id)
    setRemovendoLogo(false)
    if ('message' in result) {
      setErroLogo(result.message)
      return
    }
    setLogoInfo(null)
    setLogoIndisponivel(false)
    aplicaLogoUrl(null)
    toast.success(result.info)
  }

  // ---- Integração ERP ----
  const [chave, setChave] = useState('')
  const [token, setToken] = useState('')
  const [url, setUrl] = useState('')
  const [chaveCliente, setChaveCliente] = useState('')
  const [chaveProduto, setChaveProduto] = useState('')
  // Origem da relação Cliente x Vendedor. Default VENDA = comportamento histórico (derivada das
  // vendas); WS_W3ERP busca no WS dedicado do ERP e exige a integração ativa.
  const [chaveClienteVendedor, setChaveClienteVendedor] = useState('')
  const [origemClienteVendedor, setOrigemClienteVendedor] =
    useState<OrigemClienteVendedor>('VENDA')
  // Origem efetivamente PERSISTIDA (≠ a do Select, que o admin pode ter acabado de mudar sem
  // salvar). O backend decide o que fazer pela config gravada, então é ela que libera o botão de
  // sincronizar — senão clicar logo após trocar o Select daria "sucesso" sem sincronizar nada.
  const [origemSalva, setOrigemSalva] = useState<OrigemClienteVendedor>('VENDA')
  const [integracaoAtiva, setIntegracaoAtiva] = useState(false)
  const [erroChave, setErroChave] = useState(false)
  const [erroToken, setErroToken] = useState(false)
  const [erroUrl, setErroUrl] = useState(false)
  const [erroOrigemClienteVendedor, setErroOrigemClienteVendedor] =
    useState(false)
  const [executandoIntegracao, setExecutandoIntegracao] = useState(false)

  const carregarIntegracao = async (tenant: string) => {
    // Zera antes de buscar: a transição /novo -> /:id reaproveita a instância do componente, e
    // uma falha do GET deixaria a config do tenant anterior no formulário — o Salvar seguinte
    // gravaria a chave/origem de outra organização.
    setChave('')
    setToken('')
    setUrl('')
    setChaveCliente('')
    setChaveProduto('')
    setChaveClienteVendedor('')
    setOrigemClienteVendedor('VENDA')
    setOrigemSalva('VENDA')
    setIntegracaoAtiva(false)
    const result = await getIntegracao(tenant)
    if ('message' in result) {
      // Organização recém-criada pode não ter config ainda; não é erro de tela.
      return
    }
    // Normaliza explicitamente: o backend devolve String, então um valor novo do enum que o
    // frontend ainda não conheça viraria um `value` fora das options do Select.
    const origem: OrigemClienteVendedor =
      result.origemClienteVendedor === 'WS_W3ERP' ? 'WS_W3ERP' : 'VENDA'
    setChave(result.chave || '')
    setToken(result.token || '')
    setUrl(result.url || '')
    setChaveCliente(result.chaveCliente || '')
    setChaveProduto(result.chaveProduto || '')
    setChaveClienteVendedor(result.chaveClienteVendedor || '')
    setOrigemClienteVendedor(origem)
    setOrigemSalva(origem)
    // `=== true` porque a API devolve null quando a config nunca foi gravada (org recém-criada):
    // sem isso o switch vira controlado por null e o POST seguinte estoura o @NotNull de `ativo`.
    setIntegracaoAtiva(result.ativo === true)
  }

  // Trocar a origem ou reativar a integração é a correção do erro "exige a integração ativa" —
  // limpa a mensagem na hora, em vez de esperar a próxima tentativa de salvar.
  const handleChangeOrigemClienteVendedor = (valor: OrigemClienteVendedor) => {
    setErroOrigemClienteVendedor(false)
    setOrigemClienteVendedor(valor)
  }

  const handleChangeIntegracaoAtiva = () => {
    setErroOrigemClienteVendedor(false)
    setIntegracaoAtiva(!integracaoAtiva)
  }

  const validaCamposIntegracao = () => {
    setErroChave(false)
    setErroToken(false)
    setErroUrl(false)
    setErroOrigemClienteVendedor(false)
    if (chave.length === 0) {
      setErroChave(true)
      return false
    }
    if (token.length === 0) {
      setErroToken(true)
      return false
    }
    if (url.length === 0) {
      setErroUrl(true)
      return false
    }
    // Mesma regra do backend (VendaService.saveConfigIntegracaoW3erp): buscar a relação no WS
    // depende da integração W3ERP ativa — é de lá que saem URL e token.
    if (origemClienteVendedor === 'WS_W3ERP' && !integracaoAtiva) {
      setErroOrigemClienteVendedor(true)
      return false
    }
    return true
  }

  const salvarConfigIntegracao = async () => {
    if (!organizacao || !validaCamposIntegracao()) return
    setExecutandoIntegracao(true)
    const result = await salvarIntegracao(
      {
        ativo: integracaoAtiva,
        chave,
        chaveCliente,
        chaveProduto,
        chaveClienteVendedor,
        origemClienteVendedor,
        token,
        url,
      },
      organizacao.tenantId
    )
    setExecutandoIntegracao(false)
    if ('info' in result) {
      // A origem só passa a valer para o backend depois de gravada — é o que libera o botão de
      // sincronizar cliente x vendedor.
      setOrigemSalva(origemClienteVendedor)
      toast.success(result.info)
      return
    }
    toast.error(result.message)
  }

  const executarSync = async () => {
    if (!organizacao || !validaCamposIntegracao()) return
    setExecutandoIntegracao(true)
    const result = await getExecutarIntegracao(organizacao.tenantId)
    setExecutandoIntegracao(false)
    if ('info' in result) {
      toast.success(result.info)
      return
    }
    toast.error(result.message)
  }

  const executarEnriquecimento = async () => {
    if (!organizacao || !validaCamposIntegracao()) return
    setExecutandoIntegracao(true)
    const result = await getExecutarEnriquecimento(organizacao.tenantId)
    setExecutandoIntegracao(false)
    if ('info' in result) {
      toast.success(result.info)
      return
    }
    toast.error(result.message)
  }

  const executarClienteVendedor = async () => {
    if (!organizacao || !validaCamposIntegracao()) return
    setExecutandoIntegracao(true)
    const result = await getExecutarClienteVendedor(organizacao.tenantId)
    setExecutandoIntegracao(false)
    if ('info' in result) {
      toast.success(result.info)
      return
    }
    toast.error(result.message)
  }

  // ---- WhatsApp ----
  const [idInstancia, setIdInstancia] = useState('')
  const [tokenInstancia, setTokenInstancia] = useState('')
  const [whatsAppAtivo, setWhatsAppAtivo] = useState(false)
  const [erroIdInstancia, setErroIdInstancia] = useState(false)
  const [erroTokenInstancia, setErroTokenInstancia] = useState(false)
  const [criandoInstancia, setCriandoInstancia] = useState(false)
  const [salvandoWhatsApp, setSalvandoWhatsApp] = useState(false)

  const carregarWhatsApp = async (tenant: string) => {
    const result = await getExecutarIntegracaoWhatsApp(tenant)
    if ('message' in result) {
      return
    }
    setIdInstancia(result.idInstancia || '')
    setTokenInstancia(result.tokenInstancia || '')
    setWhatsAppAtivo(result.ativo)
  }

  const criarInstancia = async () => {
    if (!organizacao) return
    setCriandoInstancia(true)
    const result = await criarInstanciaWhatsApp(organizacao.tenantId)
    setCriandoInstancia(false)
    if ('message' in result) {
      toast.error(result.message)
      return
    }
    setIdInstancia(result.idInstancia)
    setTokenInstancia(result.tokenInstancia)
    setWhatsAppAtivo(true)
    toast.success(
      'Instância criada com sucesso! Conecte o WhatsApp na tela de Configurações.'
    )
  }

  const salvarWhatsApp = async () => {
    if (!organizacao) return
    setErroIdInstancia(false)
    setErroTokenInstancia(false)
    if (idInstancia.length === 0) {
      setErroIdInstancia(true)
      return
    }
    if (tokenInstancia.length === 0) {
      setErroTokenInstancia(true)
      return
    }
    setSalvandoWhatsApp(true)
    const result = await salvarIntegracaoWhatsApp(
      { ativo: whatsAppAtivo, idInstancia, tokenInstancia },
      organizacao.tenantId
    )
    setSalvandoWhatsApp(false)
    if ('info' in result) {
      toast.success(result.info)
      return
    }
    toast.error(result.message)
  }

  // ---- Jobs ----
  const [jobRodando, setJobRodando] = useState(false)

  const executaJob = async (
    disparo: (
      tenant?: string
    ) => Promise<{ info: string } | { message: string }>
  ) => {
    if (!organizacao) return
    setJobRodando(true)
    const result = await disparo(organizacao.tenantId)
    setJobRodando(false)
    if ('info' in result) {
      toast.success(result.info)
      return
    }
    toast.error(result.message)
  }

  const executarSnapshot = () => executaJob(getExecutarSnapshot)
  const executarReconciliacao = () => executaJob(getExecutarReconciliacao)
  const executarMixRecomendacao = () => executaJob(getExecutarMixRecomendacao)
  const executarExpurgo = () => executaJob(getExecutarExpurgo)
  // Este é SÍNCRONO no backend (uma conta só, ~segundos), ao contrário dos quatro acima que são
  // fire-and-forget. Reusa o mesmo `executaJob` porque o efeito na tela é o mesmo — botões
  // desabilitados enquanto roda e um toast no fim —, e aqui o toast fala de conclusão, não de início.
  const executarCentralResultados = () =>
    executaJob(postExecutarCentralResultados)

  // ---- Marco zero (PD-0007) ----
  // Data de implantação DECLARADA, base de toda comparação "antes x depois" da Central de
  // Resultados. É append-only no backend: declarar de novo NÃO edita, registra outra linha e a
  // anterior vira trilha. A tela reflete isso — mostra a vigente como leitura e o campo sempre
  // começa vazio, rotulado "declarar novo marco". Prefilar o campo com a data atual sugeriria
  // edição, que é justamente o que o desenho impede.
  const [marcoZero, setMarcoZero] = useState<GetMarcoZeroConsultaType | null>(
    null
  )
  const [carregandoMarcoZero, setCarregandoMarcoZero] = useState(false)
  // "Não deu para ler" != "nunca foi declarado": sem essa distinção, uma falha de rede faria a tela
  // AFIRMAR que não há marco, e o super-admin declararia um novo achando que é o primeiro —
  // trocando a vigente e poluindo a trilha por causa de um GET que falhou. Mesma separação que a
  // seção da logo faz logo acima.
  const [marcoZeroIndisponivel, setMarcoZeroIndisponivel] = useState(false)
  const [novaDataMarco, setNovaDataMarco] = useState('')
  const [novaObservacaoMarco, setNovaObservacaoMarco] = useState('')
  const [salvandoMarcoZero, setSalvandoMarcoZero] = useState(false)

  const carregarMarcoZero = async (idOrganizacao: number) => {
    setMarcoZero(null)
    setMarcoZeroIndisponivel(false)
    setCarregandoMarcoZero(true)
    const result = await getMarcoZero(idOrganizacao)
    setCarregandoMarcoZero(false)
    if (result && 'message' in result) {
      // Estado na própria seção, e não toast: o toast some e a seção continuaria mentindo. Os
      // irmãos (integração, WhatsApp) também não toastam na carga.
      setMarcoZeroIndisponivel(true)
      return
    }
    // `null` = 204 = nunca declarado. Estado normal, não erro.
    setMarcoZero(result)
  }

  const recarregarMarcoZero = () => {
    if (organizacao) carregarMarcoZero(organizacao.id)
  }

  const declararMarco = async () => {
    if (!organizacao) return
    if (!novaDataMarco) {
      toast.error('Informe a data do marco zero.')
      return
    }
    setSalvandoMarcoZero(true)
    const result = await declararMarcoZero(
      organizacao.id,
      novaDataMarco,
      novaObservacaoMarco.trim()
    )
    setSalvandoMarcoZero(false)
    if ('message' in result) {
      toast.error(result.message)
      return
    }
    // O POST já devolve vigente + trilha atualizados, então não há GET em seguida.
    setMarcoZero(result)
    setMarcoZeroIndisponivel(false)
    setNovaDataMarco('')
    setNovaObservacaoMarco('')
    toast.success('Marco zero declarado.')
  }

  // Keyado em [id]: a transição /novo -> /:id (após criar) reaproveita o mesmo
  // componente, então o efeito precisa reagir à mudança do param para carregar as
  // seções da org recém-criada. Lê a org de location.state (setada na navegação).
  useEffect(() => {
    if (isNovo) {
      setCarregando(false)
      return
    }
    const idNum = Number(id)
    const stateOrg = (location.state as { organizacao?: OrganizacaoBasica })
      ?.organizacao
    const resolve = async () => {
      setCarregando(true)
      let org = stateOrg ?? null
      if (!org) {
        const todas = await getOrganization()
        if ('message' in todas) {
          toast.error(todas.message)
          voltar()
          return
        }
        const encontrada = todas.find(item => item.id === idNum)
        if (!encontrada) {
          toast.error('Organização não encontrada.')
          voltar()
          return
        }
        org = encontrada
      }
      setOrganizacao(org)
      setNome(org.nome)
      setTenantId(org.tenantId)
      setModuloPredictAtivo(org.moduloPredictAnalyticsAtivo ?? false)
      setModuloMixAtivo(org.moduloMixInteligenteAtivo ?? false)
      setChatSuporteAtivo(org.chatSuporteAtivo)
      setFase(org.fase ?? 'EM_FUNCIONAMENTO')
      // A logo fica FORA do Promise.all: é o único binário da tela (até 1 MB, servido com
      // no-store), e prendê-la ao `carregando` atrasaria o render inteiro. A moldura tem
      // estado próprio.
      // Marco zero fica FORA do Promise.all, pelo mesmo motivo da logo: tem estado próprio na
      // seção (carregando / indisponível / declarado / nunca declarado), e prendê-lo ao
      // `carregando` global tornaria o estado "carregando" inalcançável — a tela só renderiza
      // depois — além de deixar um GET lento segurar Integração ERP e WhatsApp junto.
      carregarLogo(org.id)
      carregarMarcoZero(org.id)
      await Promise.all([
        carregarIntegracao(org.tenantId),
        carregarWhatsApp(org.tenantId),
      ])
      setCarregando(false)
    }
    resolve()
  }, [id])

  return {
    isNovo,
    carregando,
    organizacao,
    voltar,

    nome,
    setNome,
    tenantId,
    handleChangeTenant,
    moduloPredictAtivo,
    setModuloPredictAtivo,
    moduloMixAtivo,
    setModuloMixAtivo,
    chatSuporteAtivo,
    setChatSuporteAtivo,
    fase,
    setFase,
    erroNome,
    erroTenantObrigatorio,
    erroTenantInvalido,
    salvarDados,
    salvandoDados,

    logoInfo,
    logoUrl,
    carregandoLogo,
    logoIndisponivel,
    enviandoLogo,
    removendoLogo,
    erroLogo,
    enviarLogo,
    removerLogo,
    recarregarLogo,

    chave,
    setChave,
    token,
    setToken,
    url,
    setUrl,
    chaveCliente,
    setChaveCliente,
    chaveProduto,
    setChaveProduto,
    chaveClienteVendedor,
    setChaveClienteVendedor,
    origemClienteVendedor,
    handleChangeOrigemClienteVendedor,
    origemSalva,
    integracaoAtiva,
    handleChangeIntegracaoAtiva,
    erroChave,
    erroToken,
    erroUrl,
    erroOrigemClienteVendedor,
    executandoIntegracao,
    salvarConfigIntegracao,
    executarSync,
    executarEnriquecimento,
    executarClienteVendedor,

    idInstancia,
    setIdInstancia,
    tokenInstancia,
    setTokenInstancia,
    whatsAppAtivo,
    setWhatsAppAtivo,
    erroIdInstancia,
    erroTokenInstancia,
    criandoInstancia,
    salvandoWhatsApp,
    criarInstancia,
    salvarWhatsApp,

    jobRodando,
    executarSnapshot,
    executarReconciliacao,
    executarMixRecomendacao,
    executarCentralResultados,
    executarExpurgo,

    marcoZero,
    carregandoMarcoZero,
    marcoZeroIndisponivel,
    recarregarMarcoZero,
    novaDataMarco,
    setNovaDataMarco,
    novaObservacaoMarco,
    setNovaObservacaoMarco,
    salvandoMarcoZero,
    declararMarco,
  }
}
